/**
 * When each part of a bulk refresh is allowed to leave the device.
 *
 * `refreshAll()` costs six requests (seven when the venue read publishes). For
 * one phone that is fine; for a fleet arriving together — task replacement,
 * deploy, push — it is 2,100 clients doing it in the same instant. Traced
 * through `session-store.ts`, the SYNCHRONISED part is the four list reads
 * (each awaits its fetch in the first tick); the venue and nearby reads
 * already trail behind GPS-acquisition variance.
 *
 * Jitter, not stagger, is what breaks the herd. Simulated at 2,100 clients,
 * peak requests in any 100 ms window (six-together model — read absolutes as
 * an upper bound on the first window; the ratios are what the decision used):
 *
 * | schedule                          | peak   |
 * |-----------------------------------|--------|
 * | all at once                       | 12,600 |
 * | within-client stagger only        |  2,100 |
 * | per-client JITTER only            |    510 |
 * | jitter + stagger                  |    454 |
 *
 * Stagger smooths one client but every client still starts at the same
 * instant; jitter scatters the fleet because each device picks its own start.
 *
 * ## The contract
 *
 * The default is ZERO: launch preloads Cues/Inbox/Chats/Groups before their
 * tabs open (deliberate product work), launches are naturally scattered, and a
 * delay there makes a user wait for data they already had. The scheduler is
 * opt-in, and the RECONNECT path is what opts in — a dropped task disconnects
 * everyone at once and they all come back together.
 *
 * Jitter delays EVERYTHING, including the first phase (the check-in): a
 * request exempted from jitter is one the whole fleet still sends in the same
 * instant. So on reconnect the check-in may lag by up to RECONNECT_JITTER_MS,
 * during which the user is present but has not re-asserted geo-cell room
 * membership and can miss nearby events. Accepted: the socket was already down
 * longer than that, and 2,100 simultaneous check-ins — each a position write
 * plus a possible presence publish — is the thing that took a task down.
 */

/** A phase's gap from the previous one, and the work it releases. */
export type RefreshPhase = {
  /** Milliseconds to wait AFTER the previous phase before starting this one. */
  gapMs: number;
  run: () => Promise<unknown>;
};

export type RefreshScheduleOptions = {
  /**
   * Maximum random delay before the FIRST phase, in milliseconds.
   *
   * The load-bearing number. Full jitter over [0, jitterMs] — not a fixed
   * delay, which would move the herd rather than disperse it.
   */
  jitterMs?: number;
  /** Gap between phases. 0 preserves the all-at-once behaviour exactly. */
  gapMs?: number;
};

/**
 * Reconnect defaults.
 *
 * 3 s of jitter against a fleet-wide reconnect: enough to cut the peak by
 * roughly 25x, short enough that a user who was looking at the screen when the
 * socket dropped sees their data refresh within a few seconds rather than
 * wondering if the app has stalled.
 *
 * 120 ms between phases, which is below the threshold where a sequence reads
 * as slow and still separates the phases into different windows.
 */
export const RECONNECT_JITTER_MS = 3_000;
export const RECONNECT_PHASE_GAP_MS = 120;

/** A delay in [0, maxMs]. Full jitter — see the note on `jitterMs`. */
export function jitterDelayMs(maxMs: number, random: () => number = Math.random): number {
  if (!Number.isFinite(maxMs) || maxMs <= 0) return 0;
  return random() * maxMs;
}

/**
 * Run phases in order, waiting between them.
 *
 * `sleep` is injected so the ordering and the delays are testable without a
 * real clock — a scheduler tested only against wall time is a slow test suite
 * that still does not prove the order.
 *
 * Phases run SEQUENTIALLY and each is awaited, so a phase that fans out
 * internally (`Promise.all`) still completes before the next gap begins. A
 * phase that throws must not strand the ones after it: a failed inbox read is
 * not a reason to never load Nearby, and before this the whole `Promise.all`
 * would reject together.
 */
export async function runRefreshPhases(
  phases: readonly RefreshPhase[],
  {
    jitterMs = 0,
    sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)),
    random = Math.random,
    onError,
  }: {
    jitterMs?: number;
    sleep?: (ms: number) => Promise<void>;
    random?: () => number;
    /**
     * Told about a phase that threw.
     *
     * Required in practice, even though it is optional in the type. Isolating
     * the phases means `runRefreshPhases` never rejects, so a caller's own
     * `try/catch` around it is dead code — the preload failure that used to
     * reach the tab layout and get logged would otherwise vanish completely.
     * Swallowing is a decision about COUPLING (one failed read must not cancel
     * the others), never a decision to stop reporting.
     */
    onError?: (error: unknown, phaseIndex: number) => void;
  } = {},
): Promise<void> {
  const start = jitterDelayMs(jitterMs, random);
  if (start > 0) await sleep(start);

  for (const [index, phase] of phases.entries()) {
    if (phase.gapMs > 0) await sleep(phase.gapMs);
    try {
      await phase.run();
    } catch (error) {
      // Isolated per phase, deliberately: one failing read must not cancel the
      // ones after it. But isolated is not silent — `onError` is why this is
      // an observability choice rather than a swallowed defect. See its note.
      onError?.(error, index);
    }
  }
}
